import type { TargetareMatriceRand } from "@/types/targetare";
import {
  raportImplicit,
  type DefinitieRaport,
  type FiltruRapidDef,
} from "@/utils/targetareRaport";
import type { ColoanaTabel } from "@/utils/targetareTabel";

export const ETICHETE_TIP_MAPARE: Record<string, string> = {
  principal: "Cod principal în matrice",
  secundar: "Cod secundar în matrice",
  sugestie: "Sugestie (nemapat, firme pe listă)",
};

export type RandMatrice = TargetareMatriceRand & {
  cheie: string;
  versiune_text: string;
  ambiguitate_text: string;
};

export function randuriMatrice(randuri: TargetareMatriceRand[]): RandMatrice[] {
  return randuri.map((r) => ({
    ...r,
    cheie: String(r.id),
    versiune_text: `Rev. ${r.versiune}`,
    ambiguitate_text: r.ambiguitate ?? "(fără)",
  }));
}

export const COLOANE_MATRICE: ColoanaTabel<RandMatrice>[] = [
  { id: "cod", eticheta: "CAEN", tip: "text", latime: 80, implicit: true },
  {
    id: "versiune_text",
    eticheta: "Versiune",
    tip: "lista",
    latime: 80,
    implicit: true,
    optiuni: ["Rev. 2", "Rev. 3"],
  },
  {
    id: "descriere",
    eticheta: "Descriere CAEN",
    tip: "text",
    latime: 260,
    implicit: true,
  },
  {
    id: "serviciu",
    eticheta: "Serviciu NIS2",
    tip: "text",
    latime: 240,
    implicit: true,
  },
  {
    id: "entity_id",
    eticheta: "Id serviciu",
    tip: "text",
    latime: 90,
    implicit: false,
  },
  {
    id: "sector",
    eticheta: "Sector",
    tip: "lista",
    latime: 150,
    implicit: true,
  },
  {
    id: "tip_mapare",
    eticheta: "Tip mapare",
    tip: "lista",
    latime: 150,
    implicit: true,
    optiuni: ["principal", "secundar", "sugestie"],
    etichete: ETICHETE_TIP_MAPARE,
  },
  {
    id: "ambiguitate_text",
    eticheta: "Ambiguitate",
    tip: "lista",
    latime: 120,
    implicit: true,
  },
  {
    id: "scor_principal",
    eticheta: "Scor (CAEN principal)",
    tip: "numar",
    latime: 100,
    implicit: false,
  },
  {
    id: "firme_principal",
    eticheta: "Firme cu CAEN principal",
    tip: "numar",
    latime: 110,
    implicit: true,
  },
  {
    id: "firme_principal_scop",
    eticheta: "… mari/mijlocii",
    tip: "numar",
    latime: 100,
    implicit: true,
  },
  {
    id: "firme_autorizat",
    eticheta: "Firme cu cod autorizat",
    tip: "numar",
    latime: 110,
    implicit: true,
  },
  {
    id: "firme_autorizat_scop",
    eticheta: "… mari/mijlocii",
    tip: "numar",
    latime: 100,
    implicit: true,
  },
  {
    id: "firme_lista",
    eticheta: "Firme pe liste (serviciu)",
    tip: "numar",
    latime: 110,
    implicit: true,
  },
  {
    id: "firme_termene",
    eticheta: "Firme Termene (CAEN principal)",
    tip: "numar",
    latime: 120,
    implicit: false,
  },
  {
    id: "in_matrice",
    eticheta: "În matrice",
    tip: "bool",
    latime: 90,
    implicit: false,
  },
  {
    id: "categorie",
    eticheta: "Categorie",
    tip: "lista",
    latime: 90,
    implicit: false,
  },
];

export const FILTRE_RAPIDE_MATRICE: FiltruRapidDef[] = [
  { coloana: "sector", eticheta: "Sector" },
  {
    coloana: "tip_mapare",
    eticheta: "Tip mapare",
    optiuni: ["principal", "secundar", "sugestie"],
    etichete: ETICHETE_TIP_MAPARE,
  },
  {
    coloana: "versiune_text",
    eticheta: "Versiune CAEN",
    optiuni: ["Rev. 2", "Rev. 3"],
  },
  { coloana: "ambiguitate_text", eticheta: "Ambiguitate" },
  { coloana: "serviciu", eticheta: "Serviciu" },
];

const IMPLICITE = COLOANE_MATRICE.filter((c) => c.implicit).map((c) => c.id);

export const definitieRaportMatrice: DefinitieRaport<RandMatrice> = {
  cheie: "targetare.rapoarte.matrice",
  coloane: COLOANE_MATRICE,
  rapoarteImplicite: [
    raportImplicit("Matricea completă", IMPLICITE, {
      sortare: [{ id: "firme_principal_scop", desc: true }],
    }),
    raportImplicit("Coduri fără firme", IMPLICITE, {
      filtre: [
        {
          id: "f-in",
          coloana: "in_matrice",
          operator: "egal",
          valoare: "da",
          activ: true,
        },
        {
          id: "f-p",
          coloana: "firme_principal",
          operator: "egal",
          valoare: "0",
          activ: true,
        },
        {
          id: "f-a",
          coloana: "firme_autorizat",
          operator: "egal",
          valoare: "0",
          activ: true,
        },
      ],
    }),
    raportImplicit("Sugestii de completare", IMPLICITE, {
      filtre: [
        {
          id: "f-sug",
          coloana: "tip_mapare",
          operator: "egal",
          valoare: "sugestie",
          activ: true,
        },
      ],
      sortare: [{ id: "firme_principal", desc: true }],
    }),
    raportImplicit("Coduri secundare cu multe firme", IMPLICITE, {
      filtre: [
        {
          id: "f-sec",
          coloana: "tip_mapare",
          operator: "egal",
          valoare: "secundar",
          activ: true,
        },
      ],
      sortare: [{ id: "firme_autorizat_scop", desc: true }],
    }),
  ],
  cheieRand: (r) => r.cheie,
  filtreRapide: FILTRE_RAPIDE_MATRICE,
  numeFisier: "matrice-caen",
  mesajGol: "Matricea nu a fost calculată încă. Apasă „Recalculează matricea”.",
};
